import { z } from "zod";
import { AccountKindSchema, DatabaseSequenceSchema, DecimalStringSchema, LocaleSchema, Rfc3339MillisSchema, UuidSchema, WorkspaceRoleSchema, WorkspaceKindSchema, } from "./primitives.js";
export const AiPolicyModeSchema = z.enum(["review", "auto_after_validation"]);
export const LastFocusTabSchema = z.enum(["threads", "notes", "inbox"]);
export const AccountProjectionSchema = z
    .object({
    id: UuidSchema,
    kind: AccountKindSchema,
    ownedByAccountId: UuidSchema.nullable(),
    displayName: z.string().min(1).max(200),
    uiLocale: LocaleSchema,
    assistantLocale: LocaleSchema,
    timezone: z.string().min(1).max(100),
    lastFocusTab: LastFocusTabSchema,
    reducedMotion: z.boolean(),
    settingsRevision: DatabaseSequenceSchema,
    aiPolicyMode: AiPolicyModeSchema,
    aiPolicyVersion: DatabaseSequenceSchema,
    revision: DatabaseSequenceSchema,
    canSignIn: z.boolean(),
})
    .strict();
export const AccountListSchema = z.object({ items: z.array(AccountProjectionSchema) }).strict();
export const UpdateOwnAccountCommandSchema = z
    .object({
    commandId: UuidSchema,
    expectedRevision: DatabaseSequenceSchema,
    expectedSettingsRevision: DatabaseSequenceSchema,
    displayName: z.string().trim().min(1).max(200),
    uiLocale: LocaleSchema,
    assistantLocale: LocaleSchema,
    timezone: z.string().min(1).max(100),
    lastFocusTab: LastFocusTabSchema,
    reducedMotion: z.boolean(),
})
    .strict();
export const UpdateOwnAccountPreferencesCommandSchema = z
    .object({
    commandId: UuidSchema,
    expectedRevision: DatabaseSequenceSchema,
    expectedSettingsRevision: DatabaseSequenceSchema,
    uiLocale: LocaleSchema,
    reducedMotion: z.boolean(),
})
    .strict();
export const AuthSessionStateSchema = z
    .object({
    authenticated: z.boolean(),
})
    .strict();
export const AuthSignInResultSchema = z.discriminatedUnion("authenticated", [
    z
        .object({
        authenticated: z.literal(true),
        secondFactorRequired: z.literal(false),
    })
        .strict(),
    z
        .object({
        authenticated: z.literal(false),
        secondFactorRequired: z.literal(true),
    })
        .strict(),
]);
export const SetupPasskeyVerificationSchema = z
    .object({
    verified: z.literal(true),
})
    .strict();
export const AccountAiPolicyProjectionSchema = z
    .object({
    accountId: UuidSchema,
    policyMode: AiPolicyModeSchema,
    policyVersion: DatabaseSequenceSchema,
})
    .strict();
export const UpdateAccountAiPolicyCommandSchema = z
    .object({
    commandId: UuidSchema,
    expectedPolicyVersion: DatabaseSequenceSchema,
    policyMode: AiPolicyModeSchema,
})
    .strict();
export const CreateMockAccountCommandSchema = z
    .object({
    commandId: UuidSchema,
    accountId: UuidSchema,
    displayName: z.string().trim().min(1).max(200),
    uiLocale: LocaleSchema,
    assistantLocale: LocaleSchema,
    timezone: z.string().min(1).max(100),
    aiPolicyMode: AiPolicyModeSchema,
    workspaceIds: z.array(UuidSchema).max(100),
    workspaceRole: z.enum(["editor", "viewer"]),
})
    .strict();
export const WorkspaceMembershipSchema = z
    .object({
    workspaceId: UuidSchema,
    accountId: UuidSchema,
    displayName: z.string().min(1).max(200),
    accountKind: AccountKindSchema,
    canSignIn: z.boolean(),
    role: WorkspaceRoleSchema,
    membershipEpoch: DatabaseSequenceSchema,
})
    .strict();
export const WorkspaceProjectionSchema = z
    .object({
    id: UuidSchema,
    kind: WorkspaceKindSchema,
    ownerAccountId: UuidSchema,
    name: z.string().min(1).max(200),
    revision: DatabaseSequenceSchema,
    currentRole: WorkspaceRoleSchema,
    currentMembershipEpoch: DatabaseSequenceSchema,
})
    .strict();
export const WorkspaceListSchema = z
    .object({ items: z.array(WorkspaceProjectionSchema).max(100) })
    .strict();
export const WorkspaceDetailSchema = z
    .object({
    workspace: WorkspaceProjectionSchema,
    members: z.array(WorkspaceMembershipSchema),
})
    .strict();
export const CreateWorkspaceCommandSchema = z
    .object({
    commandId: UuidSchema,
    workspaceId: UuidSchema,
    name: z.string().trim().min(1).max(200),
})
    .strict();
export const UpdateWorkspaceCommandSchema = z
    .object({
    commandId: UuidSchema,
    expectedRevision: DatabaseSequenceSchema,
    name: z.string().trim().min(1).max(200),
})
    .strict();
export const UpsertWorkspaceMembershipCommandSchema = z
    .object({
    commandId: UuidSchema,
    expectedMembershipEpoch: DatabaseSequenceSchema.nullable(),
    role: WorkspaceRoleSchema,
})
    .strict();
export const RemoveWorkspaceMembershipCommandSchema = z
    .object({
    commandId: UuidSchema,
    expectedMembershipEpoch: DatabaseSequenceSchema,
})
    .strict();
export const RemovedWorkspaceMembershipSchema = z
    .object({
    removed: z.literal(true),
    workspaceAuthorizationEpoch: DatabaseSequenceSchema,
})
    .strict();
export const CreateInvitationCommandSchema = z
    .object({
    commandId: UuidSchema,
    workspaceId: UuidSchema,
    email: z.string().email().max(320).transform((value) => value.trim().toLowerCase()),
    displayName: z.string().trim().min(1).max(200),
    workspaceRole: z.enum(["editor", "viewer"]),
})
    .strict();
export const InvitationStateSchema = z.enum(["pending", "consumed", "revoked", "expired"]);
export const InvitationProjectionSchema = z
    .object({
    id: UuidSchema,
    intendedEmail: z.string().email().max(320),
    accountId: UuidSchema,
    displayName: z.string().min(1).max(200),
    workspaceId: UuidSchema,
    workspaceRole: z.enum(["editor", "viewer"]),
    state: InvitationStateSchema,
    expiresAt: Rfc3339MillisSchema,
    createdAt: Rfc3339MillisSchema,
})
    .strict();
export const InvitationListSchema = z
    .object({ items: z.array(InvitationProjectionSchema) })
    .strict();
export const SetRegistrationPasswordSchema = z
    .object({
    password: z.string().min(12).max(128),
})
    .strict();
export const VerifyRegistrationTotpSchema = z
    .object({
    code: z.string().regex(/^\d{6}$/),
})
    .strict();
export const CreateSimulationCommandSchema = z
    .object({
    commandId: UuidSchema,
    mockAccountId: UuidSchema,
    allowedWorkspaceIds: z.array(UuidSchema).min(1).max(100),
})
    .strict();
export const SimulationLinkSchema = z
    .object({
    url: z.string().url(),
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const ConsumeSimulationTokenSchema = z
    .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
})
    .strict();
export const SimulationSessionProjectionSchema = z
    .object({
    id: UuidSchema,
    actorAccountId: UuidSchema,
    effectiveAccountId: UuidSchema,
    allowedWorkspaceIds: z.array(UuidSchema).max(100),
    authorizationEpoch: DatabaseSequenceSchema,
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const RegistrationStateSchema = z.enum([
    "created",
    "passkey_options_issued",
    "passkey_verified",
    "password_set",
    "totp_verified",
    "complete",
    "expired",
]);
export const ConsumeSetupTokenSchema = z
    .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
})
    .strict();
export const RegistrationContextSchema = z
    .object({
    context: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    state: RegistrationStateSchema,
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const IdentityProjectionSchema = z
    .object({
    actor: AccountProjectionSchema,
    effective: AccountProjectionSchema,
    simulation: SimulationSessionProjectionSchema.nullable(),
})
    .strict();
//# sourceMappingURL=identity.js.map